import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { changeInterfaceHeader } from '@corefront/redux-aba';
import { useElementRef } from '../../hooks/useElementRef';
import {
  isNativeSelector,
  containerSelector,
} from '../../redux/reducers/app/params';
import { readCSSVar } from '../../utils/cssVarManipulation';
import { getScrollBarWidth } from '../../utils/getScrollbarWidth';

export const TIMEOUT = 300;

export const useDisabledNativeHeader = () => {
  const dispatch = useDispatch();
  const isNative = useSelector(isNativeSelector);

  useEffect(() => {
    if (isNative) {
      dispatch(changeInterfaceHeader(false));
    }
  }, [isNative, dispatch]);
};

export const useNavigationHeaderHeight = () => {
  const [element, ref] = useElementRef();
  const container = useSelector(containerSelector);

  useEffect(() => {
    if (element) {
      const DOM_NODE = document.querySelector(container);
      const hostHeaderHeight = readCSSVar(DOM_NODE, '--host-header-height');
      const topIndent = element.getBoundingClientRect().top;

      if (
        topIndent + Math.max(window.scrollY, 0) <= parseInt(hostHeaderHeight) &&
        DOM_NODE
      ) {
        DOM_NODE?.style?.setProperty?.(
          '--host-navigation-header-height',
          '0px'
        );
      }
    }
  }, [container, element]);

  return ref;
};

// This hook is needed to fix bugs on Windows OS where the grid breaks through the scrollbar
export const useScrollbarWidth = () => {
  const container = useSelector(containerSelector);

  const updateScrollbarWidth = useCallback(() => {
    const DOM_NODE = document.querySelector(container);
    if (DOM_NODE) {
      DOM_NODE?.style?.setProperty?.(
        '--scrollbar-width',
        `${getScrollBarWidth()}px`
      );
      DOM_NODE?.style?.setProperty?.(
        '--screen-height',
        `${window.innerHeight}px`
      );
    }
  }, [container]);

  useEffect(() => {
    updateScrollbarWidth();

    const resizeHandler = debounce(updateScrollbarWidth, TIMEOUT);
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [updateScrollbarWidth]);
};
